import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Car,
  CheckCircle2,
  ExternalLink,
  GraduationCap,
  HeartPulse,
  Home,
  Loader2,
  PiggyBank,
  Sparkles,
  Truck,
  Users,
  Utensils,
  Wifi,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/layout/PageHeader";
import { type TaxPlay, useTaxPlays } from "@/hooks/useTaxPlays";
import { formatMoney, formatPercent } from "@/lib/utils";

const categoryIcon: Record<string, typeof Truck> = {
  Vehicle: Truck,
  Home: Home,
  Communications: Wifi,
  Timing: Calendar,
  Family: Users,
  Health: HeartPulse,
  Retirement: PiggyBank,
  Operations: Utensils,
  Education: GraduationCap,
};

function splitRoute(raw: string): { path: string; hash?: string } {
  const ix = raw.indexOf("#");
  if (ix < 0) return { path: raw };
  return { path: raw.slice(0, ix) || "/", hash: raw.slice(ix + 1) };
}

export function TaxPlaysPage() {
  const data = useTaxPlays();
  const [showDisqualified, setShowDisqualified] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const plays = useMemo(() => data.data?.plays ?? [], [data.data?.plays]);
  const visible = useMemo(
    () => (showDisqualified ? plays : plays.filter((p) => p.isApplicable)),
    [plays, showDisqualified],
  );

  const totalQuantifiedSavings = useMemo(
    () =>
      visible
        .filter((p) => p.isApplicable && p.estimatedDollarImpact != null)
        .reduce((sum, p) => sum + (p.estimatedDollarImpact ?? 0), 0),
    [visible],
  );

  const blockingGaps = data.data?.projection.hasBlockingGaps;
  const marginal = data.data?.projection.nextDollarBlendedRate;

  return (
    <>
      <PageHeader
        eyebrow="Strategy"
        title="Tax Plays — concrete dollar moves"
        description="Each play is a specific tax-reduction strategy with a real mechanic, an explicit eligibility check, and a dollar impact computed from your live numbers. No bro-finance, no vibes — IRS citations on every card."
        actions={
          <div className="flex items-center gap-2">
            {marginal != null && (
              <Badge tone="info" className="gap-1">
                <Sparkles className="h-3 w-3" />
                Marginal {formatPercent(marginal, 1)}
              </Badge>
            )}
            <Badge tone={totalQuantifiedSavings > 0 ? "success" : "neutral"}>
              {formatMoney(totalQuantifiedSavings)} potential
            </Badge>
          </div>
        }
      />

      {data.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Computing plays from your live data…
        </div>
      )}

      {data.isError && (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            Couldn&apos;t load Tax Plays. Make sure the API is running.
          </CardContent>
        </Card>
      )}

      {blockingGaps && (
        <Card className="mb-4 border-amber-500/40">
          <CardContent className="flex items-start gap-3 py-4 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <div className="flex-1">
              <div className="font-semibold">Tax Intake is incomplete</div>
              <div className="text-muted-foreground">
                Plays are still listed with their eligibility and mechanics — but the dollar impact column is blank
                because we can&apos;t compute your marginal rate yet. Finish Tax Intake to see real numbers.
              </div>
            </div>
            <Link to="/tax-intake">
              <Button size="sm" variant="primary">
                Open Tax Intake
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex items-center justify-between text-xs text-muted-foreground">
        <span>{visible.length} play(s) shown · {plays.length - visible.length} hidden as not-applicable.</span>
        <button
          type="button"
          className="rounded-md border border-border px-2 py-1 hover:bg-muted/40"
          onClick={() => setShowDisqualified((s) => !s)}
        >
          {showDisqualified ? "Hide not-applicable" : "Show not-applicable"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {visible.map((play) => (
          <PlayCard
            key={play.key}
            play={play}
            isOpen={openKey === play.key}
            onToggle={() => setOpenKey((k) => (k === play.key ? null : play.key))}
          />
        ))}
      </div>
    </>
  );
}

function PlayCard({
  play,
  isOpen,
  onToggle,
}: {
  play: TaxPlay;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const Icon = categoryIcon[play.category] ?? Car;
  const { path, hash } = splitRoute(play.actionRoute);
  return (
    <Card className={play.isApplicable ? undefined : "opacity-60"}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <Icon className="h-4 w-4" />
              {play.category}
            </div>
            <CardTitle className="mt-1">{play.title}</CardTitle>
            <CardDescription className="mt-1">{play.summary}</CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="text-2xl font-bold tabular-nums">
              {formatMoney(play.estimatedDollarImpact)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              est. yr-1 saved
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {play.estimatedImpactBasis && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">How we got that number:</span>{" "}
            {play.estimatedImpactBasis}
          </div>
        )}

        {!play.isApplicable && play.disqualification && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            <span className="font-semibold">Currently not applicable:</span> {play.disqualification}
          </div>
        )}

        {isOpen && (
          <div className="space-y-3 border-t border-border pt-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                How it works
              </div>
              <pre className="mt-1 whitespace-pre-wrap text-sm leading-6 font-sans">
                {play.mechanic}
              </pre>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Eligibility
              </div>
              <ul className="mt-1 space-y-1 text-sm">
                {play.eligibility.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={onToggle}>
            {isOpen ? "Collapse" : "How it works"}
          </Button>
          {play.authorityUrl && (
            <a
              href={play.authorityUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              IRS reference
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <div className="ml-auto">
            <Link to={path} hash={hash}>
              <Button size="sm" variant="primary" disabled={!play.isApplicable}>
                {play.actionLabel}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
